import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { environment } from "../../../environments/environment";
import {
  ProductAddOnInterface,
  ProductInterface,
  QuotationInterface,
  MotorRequestInterface,
  VehicleListingInterface,
  PromoCodeInterface,
  ProductDocumentInterface,
  TRPaymentPayloadInterface,
  MotorMembershipInterface,
  ReferralCodeInterface,
  RatingFeedback,
  SystemLOVInterface
} from "../interface/motor.interface";
import { AnnouncementResponse } from "../interface/announcement.interface";
import { Observable } from "rxjs";

@Injectable({
  providedIn: "root"
})
export class MotorDataDataService {
  private basePath: string = `${environment.url}/api/Motor/`;
  private crmBasePath: string = `${environment.crmUrl}/api/Motor/MotorQuotation/`;
  private crmAnnouncementBasePath: string = `${environment.crmUrl}/api/MotorAnnouncement/`;
  private systemLOVPath: string = `${environment.url}/api/SystemLOV/`;

  constructor(private http: HttpClient) {}

  getSystemLOVByType(type: string) {
    return this.http.get<SystemLOVInterface[]>(
      `${this.systemLOVPath}GetSystemLOVByType?type=${type}`
    );
  }

  getDetailById(id: string) {
    return this.http.get<MotorRequestInterface>(
      `${this.basePath}GetRequestInfoById/${id}`
    );
  }

  getDetailByPolicyId(id: string) {
    return this.http.get<MotorRequestInterface>(
      `${this.basePath}GetRequestInfoByPolicyId/${id}`
    );
  }

  getPolicyDetailsByPaymentRefNo(id: string) {
    return this.http.get<any>(
      `${this.basePath}GetPolicyDetailsByPaymentRefNo/${id}`
    );
  }

  getMsdRequestDetailByPolicyId(id: string) {
    return this.http.get<MsdRequestData>(
      `${this.basePath}GetMsdRequestInfoByPolicyId/${id}`
    );
  }

  checkMsdRequestInfoByRequestId(id: string) {
    return this.http.get<CheckDuplicateMsd>(
      `${this.basePath}CheckMsdRequestInfoByRequestId/${id}`
    );
  }

  getMsdDocument(id: string) {
    return this.http.get<any>(
      `${this.basePath}GetDocumentDetailsByMsdRequestId/${id}`
    );
  }

  getMotorPartnerMemberByIdentityNo(identityNo: string) {
    return this.http.get<any>(
      `${this.basePath}GetMotorPartnerMemberByIdentityNo/${identityNo}`
    );
  }

  downloadFileById(id: string) {
    return this.http.get(`${this.basePath}DownloadById/${id}`, {
      responseType: "blob"
    });
  }

  getQuotationById(id: string, getProductDocument?: boolean) {
    let path = `${this.basePath}GetQuotation/${id}`;
    if (getProductDocument) {
      path += "?getProductDocument=true";
    }

    return this.http.get<QuotationInterface[]>(path);
  }

  getExpiredMotorLink(id: string) {
    let path = `${this.basePath}GetExpiredMotorLink/${id}`;
    return this.http.get<any>(path);
  }

  getListOfAnnouncement() {
    let path = `${this.crmAnnouncementBasePath}GetPublishedListing`;

    return this.http.get<AnnouncementResponse>(path);
  }

  getPartnerPaymentMethodByCode(code: string) {
    return this.http.get<any>(
      `${this.basePath}GetPartnerPaymentMethodByCode?partnerCode=${code}`
    );
  }

  getAccessList(id: string) {
    return this.http.get<any>(`${this.basePath}GetMotorExcess/${id}`);
  }

  getVehicleListing(limit: number, offset: number, text: string, make: string) {
    return this.http.post<VehicleListingInterface[]>(
      `${this.basePath}GetVehicleModelListing`,
      {
        limit,
        offset,
        text,
        make
      }
    );
  }

  getAllProduct(id: string, insuranceType: string) {
    return this.http.get(
      `${this.basePath}GetAllProduct/${id}?insuranceType=${insuranceType}`
    );
  }

  getNonQuoteProduct(id: string) {
    return this.http.get<ProductInterface[]>(
      `${this.basePath}GetNonQuoteProduct/${id}`
    );
  }

  getAddOn(id: string, quotationId: string) {
    return this.http.get<ProductAddOnInterface[]>(
      `${this.basePath}GetAddOn/${id}?quotationId=${quotationId}`
    );
  }

  getMotorMembership(id: string, quotationId: string) {
    return this.http.get<MotorMembershipInterface>(
      `${this.basePath}GetMotorMembership/${id}?quotationId=${quotationId}`
    );
  }

  applyPromoCode(
    id: string,
    code: string,
    vehicleNo: string,
    identityNo: string,
    totalCharge: number,
    motorQuotationId?: string
  ) {
    return this.http.post<PromoCodeInterface>(
      `${this.basePath}PromoCode/${id}`,
      {
        code,
        totalCharge,
        motorQuotationId,
        vehicleNo,
        identityNo
      }
    );
  }

  getPromoCodeByPartner(code: string, partner: string) {
    return this.http.get<any>(
      `${this.basePath}GetPromoCodebyPartner/${code}?partnerCode=${partner}`
    );
  }

  submitCustomerForm(id: string, val: any) {
    return this.http.post<{ isSuccess: boolean }>(
      `${this.basePath}SubmitCustomerForm/${id}`,
      val
    );
  }

getIsExistingUserDupeRequest(id: string, val: any): Observable<boolean> {
    return this.http.post<boolean>(`${this.basePath}GetIsExistingUserDupeRequest/${id}`, val);
}

  editRequest(id: string, val: any) {
    return this.http.post(`${this.basePath}EditRequest/${id}`, val, { responseType: 'text' });
  }

  getVehicleTypeAccess() {
    return this.http.get(`${this.basePath}GetMotorPartnerVehicleTypeAccess`, {
      responseType: "text"
    });
  }

  getProductDocumentById(id: string, language: string, insuranceType: string) {
    return this.http.get<ProductDocumentInterface[]>(
      `${this.basePath}GetProductDocument?id=${id}&language=${language}&insuranceType=${insuranceType}`
    );
  }

  getMotorcycleProductDocumentById(
    id: string,
    language: string,
    insuranceType: string
  ) {
    return this.http.get<ProductDocumentInterface[]>(
      `${this.basePath}GetMotorcycleProductDocument?id=${id}&language=${language}&insuranceType=${insuranceType}`
    );
  }

  generatePayment(id: string, val: any) {
    return this.http.post<{ redirectUrl: any; id: string; policyId: string }>(
      `${this.basePath}GeneratePayment/${id}`,
      val
    );
  }

  generateTopupPayment(id: string, value: TRPaymentPayloadInterface) {
    return this.http.post<{ id: string }>(
      `${this.basePath}GenerateTopUpPayment/${id}`,
      value
    );
  }

  getPaymentById(id: string) {
    return this.http.get<{
      provider: "IPay88" | "Shopback" | "Razer" | "Payex";
      data: string;
      amount: number;
    }>(`${this.basePath}GetPayment/${id}`);
  }

  checkPayment(refNo: string) {
    return this.http.get<{
      status: boolean;
      data: any;
      amount: number;
      isGeneraliIQ: boolean;
      motorPartnerId: string;
    }>(`${this.basePath}CheckPayment?refNo=${refNo}`);
  }

  uploadPhoto(id: string, file: any) {
    return this.http.post<{ id: string }>(
      `${this.basePath}UploadPolicyPhotos/${id}`,
      file
    );
  }

  addRatingFeedback(ratingFeedback: RatingFeedback) {
    return this.http.post<{ Id: string }>(
      `${this.basePath}SubmitFeedbackRating`,
      ratingFeedback
    );
  }

  deletePolicyPhoto(id: string) {
    return this.http.get(`${this.basePath}DeletePolicyPhoto/${id}`);
  }

  getPolicyInfoById(id: string) {
    return this.http.get<MotorRequestInterface>(
      `${this.basePath}GetPolicyInfoById/${id}`
    );
  }

  getPolicyPhotos(id: string, includeBase64InsteadOfUrl?: boolean) {
    let path = `${this.basePath}GetPolicyPhotos/${id}`;
    if (includeBase64InsteadOfUrl) {
      path += "?includeBase64InsteadOfUrl=true";
    }

    return this.http.get(path);
  }

  getPolicyTopUpDetail(id: string) {
    return this.http.get(`${this.basePath}GetPolicyTopUpDetail/${id}`);
  }

  editCarVariant(linkId: string, vehicleId: string) {
    return this.http.post<any>(
      `${this.basePath}EditCarVariant/${linkId}?vehicleId=${vehicleId}`,
      null
    );
  }

  getEPPRates(promoCodeId: string = null) {
    let path = `${this.basePath}GetEppRates`;

    if (promoCodeId) {
      path += `?promoCodeId=${promoCodeId}`;
    }

    return this.http.get(path);
  }

  getAvailableProduct(id: string) {
    return this.http.get(`${this.basePath}GetAvailableProductByLinkId/${id}`);
  }

  checkIsOfficeHour() {
    return this.http.get(`${this.basePath}CheckIsOfficeHour`, {
      responseType: "text"
    });
  }

  getAvailableSalesCampaign(id?: string) {
    return this.http.get(
      `${this.basePath}GetAvailableSalesCampaign?id=${id ? id : ""}`
    );
  }

  getAvailableBanners() {
    return this.http.get(`${this.basePath}GetActiveBanners`);
  }

  getBNPLRateListByPortal(portal: string) {
    return this.http.get(
      `${this.basePath}GetBNPLRateListByPortal?portal=${portal}`
    );
  }

  getGeneraliQuotationPricing(
    id: string,
    selectedAddOns: any[],
    addDriversInfo: any[],
    windshieldSI: number,
    gcktSI: number,
    cartDays: number,
    cartDailyCoverage: number
  ) {
    return this.http.post(
      `${this.crmBasePath}GetGeneraliQuotationPricing/${id}`,
      {
        selectedAddOns,
        addDriversInfo,
        windshieldSI,
        gcktSI,
        cartDays,
        cartDailyCoverage
      }
    );
  }

  getZurichQuotationPricing(
    id: string,
		selectedAddOns: any[],
		addDriversInfo: any[],
		windshieldSI: number,
		address1: string,
		address2: string,
		city: string,
		isHirePurchase: boolean,
		loanCompanyName: string,
		hirePurchaseEffDate: string | null
  ) {
    return this.http.post(`${this.crmBasePath}GetZurichQuotationPricing/${id}`, {
      SelectedAddOns: selectedAddOns,
			AdditionalDriversInfo: addDriversInfo,
			WindshieldSI: windshieldSI,
			Address1: address1,
			Address2: address2,
			City: city,
			IsHirePurchase: isHirePurchase,
			LoanCompanyName: loanCompanyName,
			HirePurchaseEffDate: hirePurchaseEffDate
    });
  }

  getAllianzQuotationPricing(
    quotationId: string, 
    selectedAddOns: any[],
    windshieldSI: number,
    cartDays: number,
    cartAmount: number,
    gcktSI: number,
    additionalDriverInfo: any[]) {
    return this.http.post(
      `${this.crmBasePath}GetAllianzQuotationPricing/${quotationId}`,
      {
        selectedAddOns,
        windshieldSI,
        cartDays,
        cartAmount,
        gcktSI,
        additionalDriverInfo
      }
    );
  }

  checkAllianzPremiumsBeforePayment(
    quotationId: string, 
    selectedAddOns: any[],
    windshieldSI: number,
    cartDays: number,
    cartAmount: number,
    gcktSI: number,
    additionalDriverInfo: any[]) {
    return this.http.post(`${this.crmBasePath}CheckAllianzE2EPremiumsBeforePayment/${quotationId}`, 
      {
        selectedAddOns,
        windshieldSI,
        cartDays,
        cartAmount,
        gcktSI,
        additionalDriverInfo
      }
    );
  }

  checkAllianzE2EQuotationValidity(quotationId: string){
    return this.http.get<boolean>(`${this.crmBasePath}CheckAllianzE2EQuotationValidity/${quotationId}`);
  }

  processGeneraliOrder(quotationId: string, infoValue: any) {
    return this.http.post(
      `${this.crmBasePath}GeneraliGetOrder/${quotationId}`,
      infoValue
    );
  }

  processChubbOrder(quotationId: string, infoValue: any){
		return this.http.post(`${this.crmBasePath}ChubbGenQuotation/${quotationId}`, infoValue);
	}

  getChubbQuotationPricing(
    id: string,
    selectedAddOns: any[],
    windshieldSI: number,
    selectedAvailableInsurerCarPlus: string
  ) {
    return this.http.post(`${this.crmBasePath}GetChubbQuotationPricing/${id}`, {
      selectedAddOns,
      windshieldSI,
      selectedAvailableInsurerCarPlus
    });
  }

  getTMIMQuotationPricing(
    selectedAddon: any[],
    windscreenValue: number | null,
    quotationId: string,
    driverCount: number | null
  ) {
    const requestData = {
      QuotationId: quotationId,
      SelectedAddon: selectedAddon,
      windscreenValue: windscreenValue,
      DriverCount: driverCount
    };
    return this.http.post(`${this.crmBasePath}GetTMIMFinalPrem`, requestData);
  }

  getSTMBQuotationPricing(
		id: string, 
		selectedAddOns: any[], 
		windshieldSI: number, 
		cartDays: number, 
		cartDailyCoverage: number,
		address: string,
		address2: string,
		city: string
	){
		return this.http.post(`${this.crmBasePath}GetSTMBQuotationPricing/${id}`, {
			selectedAddOns, 
			windshieldSI,
			cartDays,
			cartDailyCoverage,
			address,
			address2,
			city
		});
	}

  getRhbQuotationPricing(
    quotationId: string, 
    selectedAddOns: any[],
    windshieldSI: number,
    cartDays: number,
    cartAmount: number,
    gcktSI: number,
    vaSI: number,
    additionalDriverInfo: any[]) {
    return this.http.post(
      `${this.crmBasePath}GetRhbQuotationPricing/${quotationId}`,
      {
        selectedAddOns,
        windshieldSI,
        cartDays,
        cartAmount,
        gcktSI,
        vaSI,
        additionalDriverInfo
      }
    );
  }

  checkRhbPremiumsBeforePayment(
    quotationId: string, 
    selectedAddOns: any[],
    windshieldSI: number,
    cartDays: number,
    cartAmount: number,
    gcktSI: number,
    vaSI: number,
    additionalDriverInfo: any[]) {
    return this.http.post(`${this.crmBasePath}CheckRhbE2EPremiumsBeforePayment/${quotationId}`, 
      {
        selectedAddOns,
        windshieldSI,
        cartDays,
        cartAmount,
        gcktSI,
        vaSI,
        additionalDriverInfo
      }
    );
  }

  getCARTAddOnPriceList() {
    return this.http.get(`${this.basePath}GetCARTAddOnPriceList`);
  }

  secureDiscountDeal(vehicleNo: string, identityNo: string) {
    return this.http.post<any>(`${this.basePath}SecureDiscountDeal`, {
      vehicleNo,
      identityNo
    });
  }

  applyReferralCode(id: string, code: string, identityNo: string) {
    return this.http.post<ReferralCodeInterface>(
      `${this.basePath}ApplyReferralCode/${id}`,
      {
        code,
        identityNo
      }
    );
  }

  getPartnerSalesCampaignInfo(
    id: string,
    totalGrossPremium: number,
    isPromoCodeApplied: boolean
  ) {
    return this.http.post<any>(
      `${this.basePath}GetPartnerSalesCampaignInfo/${id}`,
      {
        totalGrossPremium,
        isPromoCodeApplied
      }
    );
  }

  getPartnerMember(request: PartnerMemberRequest): any {
    return this.http.post<PartnerMemberResponse>(
      `${this.basePath}GetPartnerMember`,
      request
    );
  }

  submitApplication(request: SubmitApplicationRequest) {
    return this.http.post<Response>(
      `${this.basePath}SubmitReviewApplication/${request.id}`,
      request
    );
  }

  loginReviewApplication(email: string, password: string) {
		const encoded_pass = encodeURIComponent(password);
		return this.http.post(
			`${this.basePath}LoginReviewApplication`,
			{ email, password: password },
			{ responseType: "text" },
		);
	}

  sendForgotPasswordEmail(email: string, returnUrl: string)  {
		return this.http.post(
			`${this.basePath}SendForgotPasswordEmail`,
			{ email, password: "password", returnUrl },
			{ responseType: "text" },
		);
	}

  private getHeaders(): HttpHeaders {
		const defaultHeaders: any = {
			"Content-Type": "application/json",
			responseType: "text",
		};

		return new HttpHeaders(defaultHeaders);
	}

  resetPassword(objRequest: any): Observable<any> {
		const headers = this.getHeaders();
		return this.http.post(`${this.basePath}ResetPassword`, objRequest, {
			headers,
		});
	}

	resetPasswordLinkVerify(key: any): Observable<any> {
		return this.http.get(
			this.basePath + "ResetPasswordVerify" + "?key=" + key,
			key,
		);
	}

  getUpcomingSalesCampaign(id?: string) {
    return this.http.get(
      `${this.basePath}GetUpcomingSalesCampaign?id=${id ? id : ""}`
    );
  }

  submitExitIntentFeedback(request: ExitIntentRequest) {
    return this.http.post<Response>(
      `${this.basePath}SubmitExitIntentForm`,
      request
    );
  }

  checkExitIntentFeedbackExist(motorLinkId: string) {
    return this.http.get(
      `${this.basePath}CheckExitIntentExist/${motorLinkId}`
    );
  }
}

export interface PartnerMemberRequest {
  identityNo: string;
}

export interface PartnerMemberResponse {
  isMember: boolean;
}

export interface MotorPolicyData {
  address: string;
  address2: string;
  city: string;
  state: string;
  taxPrice: number;
  total: number;
}

export interface MsdRequestData {
  name: string;
  identityNo: string;
  email: string;
  mobileNo: string;
  memberId: string;
  employeeId: string;
  officePhoneNo: string;
  officePhoneExt: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  postcode: string;
  officeAddress1: string;
  officeAddress2: string;
  officeCity: string;
  officeState: string;
  officePostcode: string;
  vehicleNo: string;
  roadtaxExpiryDate: Date;
  totalAmount: number;
  stampDuty: number;
  roadtaxRenewalPayment: number;
  monthlyInstallment: number;
  monthlyInstalmentAmt: number;
  installmentMonths: number;
  signature: string;
  signedDate: string;
  createdDate: string;
  status: string;
}

export interface SubmitApplicationRequest {
  id: string;
  actionType: "approve" | "reject";
  approverId: string;
  instalmentMonths?: number;
  monthlyInstalmentAmt?: number;
  rejectType?: string;
  rejectReason?: string;
  remarks?: string;
}

export interface CheckDuplicateMsd {
  isExist: "Y" | "N";
}

export interface ExitIntentRequest {
  motorLinkId: string;
  systemLOVId: string;
  customReason: string | null | undefined;
}
